import {httpStatus} from "../utils/index";
import BondApi from "./bond";

window._mateResponses = window._mateResponses || {};
window._mateRequests = window._mateRequests || {};


class MateApi extends BondApi {
    constructor(bondId, bondToken, btDevice) {
        let _mateId = bondId || "mate-" + crypto.randomUUID().replace(/-/g, '').slice(0, 8);
        super(_mateId, null, "bt");
        window._mateResponses[_mateId] = window._mateResponses[_mateId] || {};
        this._responses = window._mateResponses[_mateId];
        window._mateRequests[_mateId] = window._mateRequests[_mateId] || {};
        this._requests = window._mateRequests[_mateId];
        this.device = btDevice;
        if (btDevice) {
            this.device.addEventListener('gattserverdisconnected', this.handleDeviceDisconnection.bind(this));
        }
        this.server = null;
        this.service = null;
        this.characteristic = null;
        this.isConnected = false;
        this.isConnecting = false;
        this.onMessageReceived = null;
        this.onConnectionChange = null;
        this.onConnectionStarted = null;
        this.onLogMessage = null;

        // Bond Blue specific characteristics
        this.rawDataCharacteristic = null;
        this.responseCharacteristic = null;


        // Bond Blue UUIDs
        this.BOND_CLI_SERVICE_UUID = "0000fd20-cc7a-482a-984a-7f2ed5b3e58f";
        this.BOND_OTA_SERVICE_UUID = "0000fe20-cc7a-482a-984a-7f2ed5b3e58f";
        this.BOND_BLUE_SERVICE_UUID = [this.BOND_CLI_SERVICE_UUID, this.BOND_OTA_SERVICE_UUID];
        this.RAW_DATA_CHAR_UUID = "0000fd24-8e22-4541-9d4c-21edae82ed19";
        this.RESPONSE_CHAR_UUID = "0000fd23-8e22-4541-9d4c-21edae82ed19";

        // Message buffer for fragmented responses
        this.messageBuffer = new Uint8Array(0);

        // Debug mode
        this.debug = false;

        // Write queue for handling GATT operation conflicts
        this.writeQueue = [];
        this.isWriting = false;
        this.maxRetries = 3;
        this.retryDelay = 100; // ms
    }

    getMethodById(m) {
        switch (m) {
            case 0:
                return "GET";
            case 1:
                return "POST";
            case 2:
                return "PUT";
            case 3:
                return "DELETE";
            case 4:
                return "PATCH";
            default:
                return "GET";
        }
    }

    getMethodByLabel(m) {
        switch (m) {
            case "GET":
                return 0;
            case "POST":
                return 1;
            case "PUT":
                return 2;
            case "DELETE":
                return 3;
            case "PATCH":
                return 4;
            default:
                return 0;
        }
    }

    _request(path, method, body) {
        return this._btRequest(path, method, body);
    }

    _btRequest(path, method, body) {
        if (path.startsWith('/')) {
            path = path.slice(1);
        }

        let m = this.getMethodByLabel(method);

        try {
            body = JSON.parse(body);
        } catch (e) {
            body = body || {};
        }

        let requestObject = {
            "i": '70' + crypto.randomUUID().replace(/-/g, '').slice(0, 14), "t": path, "m": m, "b": body || {}
        }
        const reqId = requestObject["i"]

        this._requests[reqId] = requestObject;

        setTimeout(async () => {
            await this.sendJsonLine(requestObject);
        }, 100 + Math.random() * 100);

        return new Promise((resolve, reject) => {
            let timeout = 4000;
            let stepSleep = 50;
            const checkResponse = () => {
                if (this._responses[reqId]) {
                    const response = this._responses[reqId];
                    delete this._responses[reqId];
                    resolve(new Response(JSON.stringify(response["b"]), {"status": response["s"]}));
                } else {
                    timeout -= stepSleep;
                    if (timeout <= 0) {
                        console.warn(`[Request Timeout] ${reqId} for ${path}`);
                        resolve(new Response(null, {"status": 408}));
                    } else {
                        setTimeout(checkResponse, stepSleep);
                    }
                }
            };
            checkResponse();
        });
    }

    isSupported() {
        return navigator.bluetooth && navigator.bluetooth.requestDevice;
    }

    setDebug(enabled) {
        this.debug = enabled;
    }

    debugPrint(message) {
        if (this.debug) {
            this.logPrint(`${message}`);
        }
    }

    logPrint(message) {
        if (this.onLogMessage) {
            this.onLogMessage(message);
        }
    }

    async requestBondBlueDevice() {
        if (!this.isSupported()) {
            throw new Error('Web Bluetooth API is not supported in this browser');
        }

        const bondBlueFilters = {
            manufacturerData: [{
                companyIdentifier: 0x0F03  // New Id
                // dataPrefix: new Uint8Array([0xB0, 0x7D])
            }]
        };

        const requestOptions = {
            filters: [bondBlueFilters], optionalServices: this.BOND_BLUE_SERVICE_UUID
        };

        try {
            let _device = await navigator.bluetooth.requestDevice(requestOptions);
            this.logPrint('✅ Bond Blue device selected:', _device.name);
            return new MateApi(_device.name, null, _device);
        } catch (error) {
            this.logPrint('❌ Error requesting Bond Blue device:', error);
            throw error;
        }
    }

    async storePairedDevice() {
        let pairedDevices = await this.getParedDevices();
        pairedDevices[this.device.id] = {"bondid": this.bondId, "name": this.device.name, "id": this.device.id};
        localStorage.setItem('pairedDevices', JSON.stringify(pairedDevices));
    }

    async getParedDevices() {
        let pairedDevices = localStorage.getItem('pairedDevices');
        if (!pairedDevices) {
            return {};
        } else {
            return JSON.parse(pairedDevices);
        }
    }

    async removePairedDevice() {
        let pairedDevices = await this.getParedDevices();
        if (pairedDevices[this.device.id]) {
            delete pairedDevices[this.device.id];
        }
        localStorage.setItem('pairedDevices', JSON.stringify(pairedDevices));
    }

    async connect(retries = 3) {
        if (!this.device) {
            throw new Error('No device selected. Call requestDevice() or requestBondBlueDevice() first.');
        }

        if (this.isConnecting) {
            this.logPrint('🔄 Connection already in progress, waiting...');
            return this.server;
        }

        this.isConnecting = true;
        if (this.onConnectionStarted) this.onConnectionStarted(true);
        try {
            this.logPrint('🔗 Connecting to Bond Blue device:', this.device.name);

            // Clear any existing connection state
            this.server = null;
            this.service = null;
            this.rawDataCharacteristic = null;
            this.responseCharacteristic = null;
            this.isConnected = false;

            // Listen for disconnection events from the device
            this.device.addEventListener('gattserverdisconnected', this.handleDeviceDisconnection.bind(this));

            // Add retry logic for connection
            let lastError;
            for (let attempt = 0; attempt < retries; attempt++) {
                try {
                    await this.setupBondBlueCommunication();

                    // Only set connected to true after successful setup
                    this.isConnected = true;

                    if (this.device) {
                        await this.storePairedDevice();
                    }

                    if (this.onConnectionChange) {
                        this.onConnectionChange(true);
                    }

                    this.logPrint('✅ Connected to Bond Blue device');
                    return this.server;
                } catch (error) {
                    lastError = error;
                    this.logPrint(`❌ Connection attempt ${attempt + 1}/${retries} failed:`, error.message);

                    if (attempt < retries - 1) {
                        this.logPrint(`⏳ Retrying connection in 1 second...`);
                        await this.sleep(1000);
                    }
                }
            }

            throw lastError;

        } catch (error) {
            this.logPrint('❌ Error connecting to device:', error);
            this.isConnected = false;
            this.server = null;
            this.service = null;
            this.rawDataCharacteristic = null;
            this.responseCharacteristic = null;

            if (this.onConnectionChange) {
                this.onConnectionChange(false);
            }
            throw error;
        } finally {
            this.isConnecting = false;
            if (this.onConnectionStarted) this.onConnectionStarted(false);
        }
    }

    handleDeviceDisconnection() {
        this.logPrint('🔌 Device disconnected (gattserverdisconnected event)');

        if (this.isConnecting) {
            this.logPrint('🔄 Ignoring disconnection event during connection setup');
            return;
        }

        this.isConnected = false;
        this.server = null;
        this.service = null;
        this.rawDataCharacteristic = null;
        this.responseCharacteristic = null;

        if (this.onConnectionChange) {
            this.onConnectionChange(false);
        }
    }

    async setupBondBlueCommunication() {
        this.logPrint('🔄 Setting up Bond Blue communication...');

        if (!this.server || !this.server.connected) {
            this.server = await this.device.gatt.connect();
            if (!this.server || !this.server.connected) {
                throw new Error('Failed to connect to GATT server');
            }
        }

        this.service = await this.server.getPrimaryService(this.BOND_CLI_SERVICE_UUID);
        this.debugPrint(`Bond Blue service found: ${this.service.uuid}`);

        this.rawDataCharacteristic = await this.service.getCharacteristic(this.RAW_DATA_CHAR_UUID);
        this.debugPrint(`Raw data characteristic found: ${this.rawDataCharacteristic.uuid}`);

        this.responseCharacteristic = await this.service.getCharacteristic(this.RESPONSE_CHAR_UUID);
        this.debugPrint(`Response characteristic found: ${this.responseCharacteristic.uuid}`);

        await this.subscribeToResponses();
        this.logPrint('✅ Bond Blue communication setup complete');

        let sysVersion = {}
        while (!sysVersion || !sysVersion["bondid"]) {
            sysVersion = await this.get("sys/version")
        }
        this.bondId = sysVersion["bondid"];
        this.id = sysVersion["bondid"];
        this.storePairedDevice()
    }

    async subscribeToResponses() {
        if (!this.responseCharacteristic) {
            throw new Error('Response characteristic not available');
        }

        try {
            await this.responseCharacteristic.startNotifications();

            this.responseCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
                this.handleResponseNotification(event);
            });

            this.debugPrint('✅ Subscribed to response notifications');
            return true;
        } catch (error) {
            this.logPrint('❌ Error subscribing to notifications:', error);
            throw error;
        }
    }

    handleResponseNotification(event) {
        const value = event.target.value;
        const data = new Uint8Array(value.buffer);

        this.debugPrint(`📨 Raw notification received: ${data.length} bytes`);
        this.debugPrint(`📨 Hex data: ${Array.from(data).map(b => b.toString(16).padStart(2, '0')).join(' ')}`);

        const newBuffer = new Uint8Array(this.messageBuffer.length + data.length);
        newBuffer.set(this.messageBuffer);
        newBuffer.set(data, this.messageBuffer.length);
        this.messageBuffer = newBuffer;

        this.debugPrint(`📦 Buffer size now: ${this.messageBuffer.length} bytes`);
        this.processMessageBuffer();
    }

    processMessageBuffer() {
        try {
            const decoder = new TextDecoder('utf-8');
            const decoded = decoder.decode(this.messageBuffer, {stream: true});
            this.debugPrint(`📝 Decoded buffer: ${JSON.stringify(decoded)}`);

            if (decoded.includes('\n')) {
                const lines = decoded.split('\n');

                for (let i = 0; i < lines.length - 1; i++) {
                    const line = lines[i];
                    if (line.trim()) {
                        this.processJsonLine(line);
                    }
                }

                const lastLine = lines[lines.length - 1];
                if (lastLine) {
                    const encoder = new TextEncoder();
                    this.messageBuffer = encoder.encode(lastLine);
                    this.debugPrint(`📦 Buffer updated to contain last incomplete line: ${JSON.stringify(lastLine)}`);
                } else {
                    this.messageBuffer = new Uint8Array(0);
                    this.debugPrint('📦 Buffer cleared after processing all complete messages');
                }
            } else {
                this.debugPrint('📦 No newlines found yet - continuing to buffer');
            }

        } catch (error) {
            this.debugPrint(`❌ Error processing notification: ${error}`);
        }
    }

    async _logResponse(jsonData) {
        let topic = jsonData["t"] || "";
        let method = jsonData["m"] || 0;
        let status = jsonData["s"] || 0;
        let mateId = jsonData["B"] || "-";
        status = parseInt(status);
        let statusColor = status < 300 ? 'color: #198754' : 'color: #dc3545';
        console.groupCollapsed(`[MATE ${mateId}]\t[${this.getMethodById(method)}]\t${topic}\t%c(${status} ${httpStatus[status]})`, statusColor);

        if (this._requests[jsonData["i"]]) console.log(">\tRequest\n", this._requests[jsonData["i"]]);
        console.log("<\tResponse\n", jsonData);
        console.groupEnd();
    }

    processJsonLine(line) {
        line = line.replace(/[\x00-\x1F\x7F-\x9F]/g, '');
        try {
            const jsonData = JSON.parse(line);
            this._logResponse(jsonData);

            this._responses[jsonData["i"]] = jsonData;
            if (this.onMessageReceived) {
                this.onMessageReceived(jsonData, line);
            }

        } catch (error) {
            console.error('Error parsing JSON line:', error);
            this.logPrint('[Mate (RAW)]', line);
        }
    }

    async sendJsonLine(obj) {
        const jsonString = JSON.stringify(obj) + '\n';
        const encoder = new TextEncoder();
        const data = encoder.encode(jsonString);

        this.debugPrint(`📤 Queuing JSON line: ${jsonString.trim()}`);

        return new Promise((resolve, reject) => {
            this.writeQueue.push({
                data: data, jsonString: jsonString, resolve: resolve, reject: reject, retries: 0
            });
            this.processWriteQueue();
        });
    }

    async processWriteQueue() {
        if (this.isWriting || this.writeQueue.length === 0) {
            return;
        }

        this.isWriting = true;

        while (this.writeQueue.length > 0) {
            const writeOperation = this.writeQueue.shift();

            try {
                await this.executeWriteWithRetry(writeOperation);
                writeOperation.resolve(true);
            } catch (error) {
                writeOperation.reject(error);
            }
        }

        this.isWriting = false;
    }

    async executeWriteWithRetry(writeOperation) {
        let lastError;
        if (!this.rawDataCharacteristic) return;

        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                if (!this.rawDataCharacteristic) {
                    throw new Error('Raw data characteristic not available');
                }

                this.debugPrint(`📤 Attempt ${attempt + 1}/${this.maxRetries + 1} to send: ${writeOperation.jsonString.trim()}`);
                await this.rawDataCharacteristic.writeValue(writeOperation.data);
                this.debugPrint('✅ JSON line sent successfully');
                return true;

            } catch (error) {
                lastError = error;
                writeOperation.retries = attempt + 1;

                this.debugPrint(`❌ Write attempt ${attempt + 1} failed: ${error.message}`);

                if (error.message.includes('GATT operation already in progress') || error.message.includes('Operation already in progress')) {
                    if (attempt < this.maxRetries) {
                        this.debugPrint(`⏳ Retrying in ${this.retryDelay}ms...`);
                        await this.sleep(this.retryDelay);
                        this.retryDelay = Math.min(this.retryDelay * 1.5, 1000);
                    } else {
                        this.debugPrint(`❌ Max retries reached for: ${writeOperation.jsonString.trim()}`);
                    }
                } else {
                    this.debugPrint(`❌ Non-retryable error: ${error.message}`);
                    break;
                }
            }
        }

        throw lastError || new Error('Write operation failed after all retries');
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    isDeviceConnected() {
        return this.device && this.server && this.server.connected && this.isConnected && this.service && this.rawDataCharacteristic && this.responseCharacteristic;
    }

    async disconnect() {
        this.clearWriteQueue();

        if (this.device) {
            this.device.removeEventListener('gattserverdisconnected', this.handleDeviceDisconnection.bind(this));
        }

        if (this.server && this.server.connected) {
            try {
                await this.server.disconnect();
            } catch (error) {
                console.warn('Error during disconnect:', error);
            }
        }

        this.server = null;
        this.service = null;
        this.characteristic = null;
        this.rawDataCharacteristic = null;
        this.responseCharacteristic = null;
        this.isConnected = false;
        this.messageBuffer = new Uint8Array(0);

        if (this.onConnectionChange) {
            this.onConnectionChange(false);
        }
    }

    async getService(serviceUuid) {
        if (!this.server || !this.server.connected) {
            throw new Error('Not connected to device');
        }

        try {
            this.service = await this.server.getPrimaryService(serviceUuid);
            this.logPrint('Service found:', this.service.uuid);
            return this.service;
        } catch (error) {
            console.error('Error getting service:', error);
            throw error;
        }
    }

    async getCharacteristic(characteristicUuid, serviceUuid = null) {
        const service = serviceUuid ? await this.getService(serviceUuid) : this.service;
        if (!service) throw new Error('No service available');

        try {
            this.characteristic = await service.getCharacteristic(characteristicUuid);
            this.logPrint('Characteristic found:', this.characteristic.uuid);
            return this.characteristic;
        } catch (error) {
            console.error('Error getting characteristic:', error);
            throw error;
        }
    }

    async sendMessage(message, characteristicUuid = null, serviceUuid = null) {
        let characteristic = this.characteristic;

        if (characteristicUuid) characteristic = await this.getCharacteristic(characteristicUuid, serviceUuid);

        if (!characteristic) throw new Error('No characteristic available');

        const encoder = new TextEncoder();
        const messageBuffer = encoder.encode(message);

        try {
            await characteristic.writeValue(messageBuffer);
            this.logPrint('Message sent:', message);
            return true;
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    setMessageCallback(callback) {
        this.onMessageReceived = callback;
    }

    setConnectionCallback(callback) {
        this.onConnectionChange = callback;
    }

    setConnectionStartedCallback(callback) {
        this.onConnectionStarted = callback;
    }

    setOnLogMessage(callback) {
        this.onLogMessage = callback;
    }

    getConnectionStatus() {
        return {
            isConnected: this.isConnected,
            deviceName: this.device ? this.device.name : null,
            deviceId: this.device ? this.device.id : null,
            hasBondBlueService: this.service !== null,
            hasRawDataChar: this.rawDataCharacteristic !== null,
            hasResponseChar: this.responseCharacteristic !== null
        };
    }

    clearWriteQueue() {
        this.writeQueue = [];
        this.isWriting = false;
    }

    async searchPairedDevices() {
        let _availableDevices = [];
        if (navigator.bluetooth && navigator.bluetooth.getDevices === undefined) {
            return [];
        }
        let pairedDevices = await this.getParedDevices();
        if (Object.keys(pairedDevices).length === 0) {
            this.logPrint('No paired devices found');
            return [];
        } else {
            let _btDevices = await navigator.bluetooth.getDevices();
            this.logPrint('🔍 Found devices:', _btDevices);
            for (let i = 0; i < _btDevices.length; i++) {
                let _btDevice = _btDevices[i];
                await _btDevice.watchAdvertisements();
                if (pairedDevices[_btDevice.id]) {
                    let _bondId = pairedDevices[_btDevice.id].bondid;
                    _availableDevices.push({
                        bondid: _bondId, mate: new MateApi(_bondId, null, _btDevice)
                    });
                }
            }
            return _availableDevices;
        }
    }
}

export default MateApi;
